import { colorChannel } from '@mui/system/colorManipulator';

import constants from '../../designTokens/build/constants';

import { Tokens } from './tokens';

import type { ColorScheme } from './types';
import type { ColorSystemOptions } from '@mui/material/styles';

const mapTokenColorToMui = (
  colorObj: Tokens['color'][
    | 'info'
    | 'error'
    | 'primary'
    | 'secondary'
    | 'neutral'
    | 'success'
    | 'warning'],
  mode: 'light' | 'dark'
) => {
  return {
    1: colorObj['1'][mode],
    2: colorObj['2'][mode],
    3: colorObj['3'][mode],
    4: colorObj['4'][mode],
    5: colorObj['5'][mode],
    6: colorObj['6'][mode],
    7: colorObj['7'][mode],
    8: colorObj['8'][mode],
    9: colorObj['9'][mode],
    10: colorObj['10'][mode],
    11: colorObj['11'][mode],
    12: colorObj['12'][mode],
    a1: colorObj['a1'][mode],
    a2: colorObj['a2'][mode],
    a3: colorObj['a3'][mode],
    a4: colorObj['a4'][mode],
    a5: colorObj['a5'][mode],
    a6: colorObj['a6'][mode],
    a7: colorObj['a7'][mode],
    a8: colorObj['a8'][mode],
    a9: colorObj['a9'][mode],
    a10: colorObj['a10'][mode],
    a11: colorObj['a11'][mode],
    a12: colorObj['a12'][mode],

    50: colorObj['50'][mode],
    100: colorObj['100'][mode],
    200: colorObj['200'][mode],
    300: colorObj['300'][mode],
    400: colorObj['400'][mode],
    500: colorObj['500'][mode],
    600: colorObj['600'][mode],
    700: colorObj['700'][mode],
    800: colorObj['800'][mode],
    900: colorObj['900'][mode],
    950: colorObj['950'][mode],

    '50Channel': colorChannel(colorObj['50'][mode]),
    '100Channel': colorChannel(colorObj['100'][mode]),
    '200Channel': colorChannel(colorObj['200'][mode]),
    '300Channel': colorChannel(colorObj['300'][mode]),
    '400Channel': colorChannel(colorObj['400'][mode]),
    '500Channel': colorChannel(colorObj['500'][mode]),
    '600Channel': colorChannel(colorObj['600'][mode]),
    '700Channel': colorChannel(colorObj['700'][mode]),
    '800Channel': colorChannel(colorObj['800'][mode]),
    '900Channel': colorChannel(colorObj['900'][mode]),
    '950Channel': colorChannel(colorObj['950'][mode]),

    lightest: colorObj['lightest'][mode],
    light: colorObj['light'][mode],
    main: colorObj['main'][mode],
    dark: colorObj['dark'][mode],
    darkest: colorObj['darkest'][mode],

    lightestChannel: colorChannel(colorObj['lightest'][mode]),
    lightChannel: colorChannel(colorObj['light'][mode]),
    mainChannel: colorChannel(colorObj['main'][mode]),
    darkChannel: colorChannel(colorObj['dark'][mode]),
    darkestChannel: colorChannel(colorObj['darkest'][mode]),

    og: colorObj['main'][mode],
    ogSmart: colorObj['main'][mode],

    backgroundSubtlest: colorObj['backgroundSubtlest'][mode],
    backgroundSubtle: colorObj['backgroundSubtle'][mode],
    backgroundSoft: colorObj['backgroundSoft'][mode],
    backgroundSoftHover: colorObj['backgroundSoftHover'][mode],
    borderRegular: colorObj['borderRegular'][mode],
    borderStrong: colorObj['borderStrong'][mode],
    solid: colorObj['solid'][mode],
    solidHover: colorObj['solidHover'][mode],
    text: colorObj['text'][mode],
    contrastText: colorObj['contrast'][mode] || '#FFF',

    activated: `rgba(${colorChannel(colorObj['main'][mode])} / ${
      constants.color.action['activatedOpacity'][mode]
    })`,
    hovered: `rgba(${colorChannel(colorObj['main'][mode])} / ${
      constants.color.action['hoverOpacity'][mode]
    })`,
    selected: `rgba(${colorChannel(colorObj['main'][mode])} / ${
      constants.color.action['selectedOpacity'][mode]
    })`,
  };
};

const generatePaletteForMode = (colors, mode: 'light' | 'dark') => {
  return {
    action: {
      active: colors.action['active'][mode],
      hover: colors.action['hover'][mode],
      hoverOpacity: constants.color.action['hoverOpacity'][mode],
      selected: colors.action['selected'][mode],
      selectedOpacity: constants.color.action['selectedOpacity'][mode],
      disabled: colors.action['disabled'][mode],
      disabledOpacity: constants.color.action['disabledOpacity'][mode],
      focus: colors.action['focus'][mode],
      focusOpacity: constants.color.action['focusOpacity'][mode],
      activated: colors.action['activated'][mode],
      activatedOpacity: constants.color.action['activatedOpacity'][mode],
      disabledBackground: colors.action['disabledBackground'][mode],
    },
    background: {
      default: colors.background.primary[mode],
      defaultChannel: colorChannel(colors.background.primary[mode]),
      paper: colors.paper.background[mode],
      paperChannel: colorChannel(colors.paper.background[mode]),
      level1: colors.background['level1'][mode],
      level1Channel: colorChannel(colors.background['level1'][mode]),
      level2: colors.background['level2'][mode],
      level2Channel: colorChannel(colors.background['level2'][mode]),
      level3: colors.background['level3'][mode],
      level3Channel: colorChannel(colors.background['level3'][mode]),
    },
    common: {
      black: colors.black[mode],
      white: colors.white[mode],
    },
    divider: colors.divider['regular'][mode],
    error: mapTokenColorToMui(colors.error, mode),
    info: mapTokenColorToMui(colors.info, mode),
    primary: mapTokenColorToMui(colors.primary, mode),
    secondary: mapTokenColorToMui(colors.secondary, mode),
    success: mapTokenColorToMui(colors.success, mode),
    warning: mapTokenColorToMui(colors.warning, mode),
    alert: mapTokenColorToMui(colors.primary, mode),
    neutral: mapTokenColorToMui(colors.neutral, mode),
    text: {
      primary: colors.text.primary[mode],
      secondary: colors.text.secondary[mode],
      tertiary: colors.text.tertiary[mode],
      quaternary: colors.text.quaternary[mode],
      disabled: colors.text.disabled[mode],
    },

    Avatar: { defaultBg: colors.avatar.background[mode] },
    Backdrop: { bg: 'rgba(0, 0, 0, 0.5)' },
    OutlinedInput: {
      border: colors.field.outlined['borderEnabled'][mode],
    },
    TableCell: { border: 'var(--mui-palette-divider)' },
    Tooltip: { bg: 'rgba(10, 13, 20, 0.75)' },
  };
};

export function colorSchemes(
  colors: Tokens['color']
): Partial<Record<ColorScheme, ColorSystemOptions>> {
  return {
    dark: {
      palette: generatePaletteForMode(colors, 'dark'),
    },
    light: {
      palette: generatePaletteForMode(colors, 'light'),
    },
  };
}
